import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import BaseModal from "./BaseModal";
import BaseButton from "../buttons/BaseButton";
import BaseCancel from "../buttons/BaseCancel";
import "./layouts-styles.css";

/**
 * The one place the app asks "are you sure?".
 *
 * Confirmation used to be an antd Popconfirm hanging off whichever
 * control triggered it, which meant a bubble that could open off-screen
 * from a table row, no room for what the action would actually do, and
 * eight different phrasings of the same question — including two literal
 * "Are you sure?"s that named neither the thing nor the consequence.
 *
 * A confirm dialog in a case-management tool has to answer three things:
 * what am I about to do, what changes as a result, and can I take it
 * back. `outcome` is the second one, and it is the reason this exists as
 * a dialog rather than a bubble: there is finally room to say "the case
 * returns to New Cases, unassigned" in the same vocabulary the case
 * tables use.
 *
 *   const confirm = useConfirm();
 *   if (!await confirm({ tone: 'danger', title: 'Delete this case?', ... })) return;
 *
 * Pass `action` when the work is a request rather than local state. The
 * dialog then holds itself open with the commit button working until the
 * promise settles, instead of vanishing and leaving the page looking
 * like the click did nothing. Approving a case took a round trip and the
 * page had no loading state of its own past the first render, so it sat
 * unchanged until the refetch landed.
 *
 * Mount <ConfirmProvider> once, above everything that might ask.
 */

const ConfirmContext = createContext(null);

/* Tone drives the mark and the commit button. Nothing else is coloured. */
const TONE = {
  danger: { icon: "tabler:alert-triangle", confirm: { danger: true, type: "primary" } },
  warning: { icon: "tabler:alert-triangle", confirm: { type: "primary" } },
  primary: { icon: "tabler:help-circle", confirm: { type: "primary" } },
};

const DEFAULTS = {
  tone: "primary",
  confirmText: "Confirm",
  cancelText: "Cancel",
};

export const ConfirmProvider = ({ children }) => {
  const [options, setOptions] = useState(null);
  const [running, setRunning] = useState(false);
  const resolver = useRef(null);

  const confirm = useCallback((next) => {
    setOptions({ ...DEFAULTS, ...next });
    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = (answer) => {
    setOptions(null);
    setRunning(false);
    if (resolver.current) {
      resolver.current(answer);
      resolver.current = null;
    }
  };

  const onConfirm = async () => {
    if (!options || !options.action) {
      settle(true);
      return;
    }

    setRunning(true);
    try {
      await options.action();
    } catch (e) {
      // The call site reports its own outcome — makeRequest catches and
      // toasts. Swallowing here only stops a rejected action becoming an
      // unhandled rejection out of a click handler; it still gets logged.
      console.error("Confirmed action failed", e);
    } finally {
      settle(true);
    }
  };

  const tone = options ? (TONE[options.tone] || TONE.primary) : TONE.primary;

  return (
    <ConfirmContext.Provider value={ confirm }>
      { children }

      <BaseModal
        className={ `base-modal confirm-modal confirm-modal--${options ? options.tone : "primary"}` }
        icon={ options ? (options.icon || tone.icon) : tone.icon }
        mainTitle={ options ? options.title : "" }
        subTitle={ options ? options.subtitle : undefined }
        open={ !!options }
        onCancel={ () => !running && settle(false) }
        closable={ !running }
        keyboard={ !running }
        width="min(460px, calc(100vw - 32px))"
        footer={
          <div className="form-actions">
            <BaseCancel
              onClick={ () => settle(false) }
              disabled={ running }
              text={ options ? options.cancelText : DEFAULTS.cancelText }
              /* On a destructive prompt the safe way out holds the focus,
                 so a stray Enter cannot delete anything. */
              autoFocus={ !!options && options.tone === "danger" }
            />
            <BaseButton
              { ...tone.confirm }
              autoFocus={ !!options && options.tone !== "danger" }
              state={ running }
              onClick={ onConfirm }
            >
              { options ? options.confirmText : DEFAULTS.confirmText }
            </BaseButton>
          </div>
        }
      >
        { options && options.description &&
          <p className="confirm-body">{ options.description }</p> }

        { options && options.outcome &&
          <p className="confirm-outcome">
            <Icon icon="tabler:arrow-narrow-right" width={ 17 } />
            <span>{ options.outcome }</span>
          </p> }
      </BaseModal>
    </ConfirmContext.Provider>
  );
};

/** Returns confirm(options) -> Promise<boolean>. */
export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);

  if (!confirm) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>");
  }

  return confirm;
};

export default ConfirmProvider;
